import { Chip } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"
import React from "react"

import { CasNumbersCell } from "../components/CasNumbersCell"
import CompetitorRowActions from "../components/CompetitorRowActions"
import { DistributorsCell } from "../components/DistributorsCell"
import { FileIconCell } from "../components/FileIconCell"

import { useCompetitorProductPermissions } from "./useCompetitorProductPermissions"

import type { CompetitorProduct } from "@/graphql/codegen/graphql"

interface UseCompetitorProductsColumnsProps {
  onEdit?: (product: CompetitorProduct) => void
  onDelete?: (competitorProductId: string, productName: string) => Promise<boolean>
}

export const useCompetitorProductsColumns = ({
  onEdit,
  onDelete,
}: UseCompetitorProductsColumnsProps = {}): ColumnDef<CompetitorProduct>[] => {
  const { hasFullAccess } = useCompetitorProductPermissions()

  const columns: ColumnDef<CompetitorProduct>[] = [
    {
      accessorKey: "productName",
      header: "Product Name",
      sortingFn: "alphanumeric",
      cell: ({ row }) => <div className='flex grow items-center'>{row.original.productName}</div>,
      minSize: 200,
      enableSorting: true,
    },
    {
      accessorKey: "chemicalName",
      header: "Chemical Name",
      sortingFn: "alphanumeric",
      cell: ({ row }) => <div className='flex items-center'>{row.original.chemicalName || ""}</div>,
      minSize: 150,
      enableSorting: true,
    },
    {
      accessorKey: "casNumbers",
      header: "CAS",
      sortingFn: "alphanumeric",
      cell: ({ row }) => <CasNumbersCell casNumbers={row.original.casNumbers} />,
      minSize: 120,
      enableSorting: false,
    },
    {
      accessorKey: "manufacturer",
      header: "Manufacturer",
      sortingFn: "alphanumeric",
      cell: ({ row }) => (
        <div className='flex items-center'>
          {row.original.manufacturer && (
            <Chip
              label={row.original.manufacturer}
              size='small'
              className='rounded bg-primary-200 text-xs text-gray-900'
            />
          )}
        </div>
      ),
      minSize: 150,
      enableSorting: true,
    },
    {
      accessorKey: "distributors",
      header: "Distributor",
      cell: ({ row }) => <DistributorsCell distributors={row.original.distributors} />,
      minSize: 200,
      enableSorting: false,
    },
    {
      accessorKey: "technicalFile",
      header: "",
      cell: ({ row }) =>
        row.original.hasFile ? <FileIconCell competitorProductId={row.original.competitorProductId} /> : null,
      size: 50,
      enableSorting: false,
    },
  ]

  // Only add actions column if user has full access
  if (hasFullAccess) {
    columns.push({
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => <CompetitorRowActions row={row} onEdit={onEdit} onDelete={onDelete} />,
      size: 50,
      enableSorting: false,
    })
  }

  return columns
}
